/*
 * Purpose: Split-flap headline board — predefined line breaks per phrase
 * Last modified: 2026-06-27 | Agent
 * Completeness: 98
 */
import { PRODUCT_BRAND } from '@/lib/productTerms';

export type HeadlineBoardDef = {
  /** Full phrase for screen readers */
  text: string;
  /** Explicit row breaks — each string is one departure-board line */
  lines: readonly [string, ...string[]];
};

/** Predefined line breaks for each rotating home headline */
export const HOME_HEADLINE_BOARDS: readonly HeadlineBoardDef[] = [
  {
    text: 'Describe your outcome?',
    lines: ['Describe your', 'outcome?'],
  },
  {
    text: 'What should happen automatically?',
    lines: ['What should happen', 'automatically?'],
  },
  {
    text: 'Any new ideas to explore?',
    lines: ['Any new ideas', 'to explore?'],
  },
  {
    text: `What do you want ${PRODUCT_BRAND} to do?`,
    lines: ['What do you want', `${PRODUCT_BRAND} to do?`],
  },
] as const;

export const HEADLINE_BOARD_ROWS = Math.max(
  ...HOME_HEADLINE_BOARDS.map((b) => b.lines.length),
);

export const HEADLINE_ROW_SLOTS = Math.max(
  ...HOME_HEADLINE_BOARDS.flatMap((b) => b.lines.map((line) => line.length)),
);

export const SPLIT_FLAP_SLOT_COUNT = HEADLINE_BOARD_ROWS * HEADLINE_ROW_SLOTS;

export type BoardSlot = {
  char: string;
  row: number;
  col: number;
  slotIndex: number;
  /** Last visible column index (exclusive) for this row in the active headline */
  rowVisibleLength: number;
};

/** Flat slot grid for a headline — stable indices across phrase changes */
export function boardSlotsForHeadline(board: HeadlineBoardDef): BoardSlot[] {
  const slots: BoardSlot[] = [];

  for (let row = 0; row < HEADLINE_BOARD_ROWS; row += 1) {
    const line = board.lines[row] ?? '';
    const padded = line.padEnd(HEADLINE_ROW_SLOTS, ' ');

    for (let col = 0; col < HEADLINE_ROW_SLOTS; col += 1) {
      slots.push({
        char: padded[col] ?? ' ',
        row,
        col,
        slotIndex: row * HEADLINE_ROW_SLOTS + col,
        rowVisibleLength: line.length,
      });
    }
  }

  return slots;
}
